<template>
  <div>
    <label
      class="block mb-2 text-sm font-medium text-gray-900 dark:text-white sr-only"
      >{{ label }}</label
    >
    <ais-range-input :attribute="attribute">
      <template
        v-slot="{ currentRefinement, range, canRefine, refine, sendEvent }"
      >
        <div @vue:mounted="initSlotDropdown">
          <button
            type="button"
            :id="itemKey('button')"
            :data-dropdown-toggle="itemKey('dropdown')"
            class="flex justify-between items-center bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full px-2.5 py-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 xl:min-w-[160px] 2xl:min-w-[180px]"
          >
            <span class="flex-1 text-left truncate">{{
              buttonLabel(currentRefinement)
            }}</span>
            <svg
              class="w-4 h-4 ms-2"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>

          <div
            :id="itemKey('dropdown')"
            class="z-20 hidden bg-white rounded-lg shadow w-60 dark:bg-gray-700"
          >
            <div class="z-20 bg-white rounded-lg shadow w-72 dark:bg-gray-700">
              <!-- Increased width of the container -->
              <div class="flex items-center p-4 justify-between">
                <div class="relative w-5/12 mr-1">
                  <!-- Adjusted width -->
                  <div
                    class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                  >
                    <span class="text-gray-500 dark:text-gray-400 sm:text-sm uppercase">{{ currency }}</span>
                  </div>
                  <input
                    type="number"
                    :min="range.min"
                    :max="range.max"
                    :placeholder="range.min.toString()"
                    :disabled="!canRefine"
                    class="block pl-12 pr-2 sm:text-sm border-gray-300 rounded-md w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    :value="formatMinValue(currentRefinement.min, range.min)"
                    @input="
                      handleInput(
                        $event.currentTarget.value,
                        formatMaxValue(currentRefinement.max, range.max),
                        refine
                      )
                    "
                  />
                </div>
                <span class="mx-1 text-sm">{{$t('labels.to')}}</span>
                <!-- Adjusted margin for 'to' label -->
                <div class="relative w-5/12">
                  <!-- Adjusted width -->
                  <div
                    class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                  >
                    <span class="text-gray-500 dark:text-gray-400 sm:text-sm uppercase">{{ currency }}</span>
                  </div>
                  <input
                    type="number"
                    :min="range.min"
                    :max="range.max"
                    :placeholder="range.max.toString()"
                    :disabled="!canRefine"
                    class="block pl-12 pr-2 sm:text-sm border-gray-300 rounded-md w-full dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    :value="formatMaxValue(currentRefinement.max, range.max)"
                    @input="
                      handleInput(
                        formatMinValue(currentRefinement.min, range.min),
                        $event.currentTarget.value,
                        refine
                      )
                    "
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </ais-range-input>
  </div>
</template>
<script setup lang="ts">
import { AisRangeInput } from "vue-instantsearch/vue3/es";
import { initDropdowns } from "flowbite";

const { track } = useAnalytics();

const props = defineProps({
  label: {
    label: String,
    required: true,
  },
  currency: {
    label: String,
    default: "USD",
  },
  attribute: {
    label: String,
    required: true,
  },
});

let refineTimeout = null;

function handleInput(minValue, maxValue, refine) {
  if (maxValue && maxValue.length < 3) {
    maxValue = null;
  }
  // A max below the min is an invalid range (usually the user is still
  // typing) — search with the min only instead of flashing empty results.
  if (minValue && maxValue && Number(maxValue) < Number(minValue)) {
    maxValue = null;
  }
  // Debounce so results only refresh once the user pauses typing.
  clearTimeout(refineTimeout);
  refineTimeout = setTimeout(() => {
    refine({
      min: minValue,
      max: maxValue,
    });
    track("search_filter", { filter: "price", min: minValue, max: maxValue });
  }, 400);
}

onBeforeUnmount(() => clearTimeout(refineTimeout));

function itemKey(prefix, id = null) {
  return (
    props.attribute.replaceAll(".", "-") +
    "-" +
    prefix +
    "-item" +
    (id ? "-" + id : "")
  );
}

function formatMinValue(minValue, minRange) {
  return minValue !== null && minValue !== minRange ? minValue : "";
}
function formatMaxValue(maxValue, maxRange) {
  return maxValue !== null && maxValue !== maxRange ? maxValue : "";
}

// Show the active refinement on the button ("USD 1000+", "USD 1600 - USD 4600"),
// falling back to the generic label when nothing is set. AisRangeInput already
// normalizes currentRefinement: min/max are undefined when unset or equal to
// the index bounds.
function buttonLabel(currentRefinement) {
  const { min, max } = currentRefinement;
  const currency = String(props.currency).toUpperCase();

  if (min != null && max != null) {
    return `${currency} ${min} - ${max}`;
  }
  if (min != null) {
    return `${currency} ${min}+`;
  }
  if (max != null) {
    return `${currency} 0 - ${max}`;
  }
  return props.label;
}

// The button lives inside the ais-range-input slot (so it can show the current
// refinement), which renders after this component mounts — init the Flowbite
// dropdown when the slot content actually enters the DOM.
function initSlotDropdown() {
  nextTick(() => initDropdowns());
}
</script>
